import React from 'react';
import styled from 'styled-components';
import { Box, Grid } from '@mui/material';
import {
  Button,
  FILE_MANAGE_ORDER,
  FilesPanel,
  SwitchButton,
  type FileData,
} from '../../../../../components';
import { PAGE_TEXT } from '../lang';
import { useFormik, type FormikValues } from 'formik';
import { object, string } from 'yup';
import type { EmploymentBoxInfo } from './types';
import { returnedComponent } from '../../returnFieldComponent';
import { employmentFieldOptions, employmentModeOptions } from './options';
import type { EmploymentFormData, EmploymentFormValue } from '../../../graphql';
import { handleReadySubmit } from '../../../../../utils';

export type Formik<T extends FormikValues> = ReturnType<typeof useFormik<T>>;
export type FormikProp = Formik<EmploymentFormValue>;

const pageText = PAGE_TEXT.OTHER_INFORMATION.EMPLOYMENT;

type EmploymentBoxProps = {
  initialEmploymentValues: EmploymentFormData;
  enableEdit: boolean;
  enableDelete: boolean;
  employmentIndex: number;
  handleSubmit: (values: EmploymentFormData) => void;
  handleDelete: (e: React.MouseEvent) => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

export const EmploymentBox: React.FunctionComponent<EmploymentBoxProps> = (
  props: EmploymentBoxProps,
) => {
  const {
    initialEmploymentValues,
    enableEdit,
    employmentIndex,
    handleDelete,
    enableDelete,
    handleSubmit,
    handleLoading,
  } = props;
  const { supportingDocuments: initialSupportingDocuments = [], ...others } =
    initialEmploymentValues;

  const [supportingDocuments, setSupportingDocuments] = React.useState<
    FileData[]
  >(initialSupportingDocuments);

  const handleFormSubmit = (values: EmploymentFormValue): void => {
    handleSubmit({ ...values, supportingDocuments });
  };

  const formik = useFormik<EmploymentFormValue>({
    initialValues: {
      ...others,
    },
    validationSchema: object({
      from: string().required('Required'),
      to: string().when(
        'presentEmploymentIndicator',
        // make it boolean to avoid type error
        ([presentEmploymentIndicator]: boolean[], schema) => {
          return presentEmploymentIndicator
            ? schema.notRequired()
            : schema.required('Required');
        },
      ),
      employmentMode: string().required('Required'),
      employmentField: string().required('Required'),
      organisationName: string().required('Required'),
      position: string().required('Required'),
      majorResponsibilities: string().when((_, schema) => {
        return schema.required('Required').test({
          message: 'Over word limit',
          test: value => value.length <= 750,
        });
      }),
    }),
    onSubmit: handleFormSubmit,
  });

  const employmentBoxInfo: EmploymentBoxInfo = {
    subTitle: pageText.TITLE,
    switchText: pageText.PRESENT,
    initialForm: [
      {
        label: pageText.FROM,
        type: 'date',
        required: true,
        name: 'from',
        minDate: null,
        maxDate: undefined,
        disabled: !enableEdit,
      },
      {
        label: pageText.TO,
        type: 'date',
        required: true,
        name: 'to',
        minDate: formik.values.from,
        maxDate: undefined,
        disabled: !enableEdit || formik.values.presentEmploymentIndicator,
      },
      {
        label: pageText.MODE_OF_EMPLOYMENT,
        type: 'select',
        options: employmentModeOptions,
        required: true,
        name: 'employmentMode',
        disabled: !enableEdit,
      },
      {
        label: pageText.FIELD_OF_EMPLOYMENT,
        type: 'select',
        options: employmentFieldOptions,
        required: true,
        name: 'employmentField',
        disabled: !enableEdit,
      },
      {
        label: pageText.ORGANISATION_NAME,
        type: 'text',
        required: true,
        name: 'organisationName',
        disabled: !enableEdit,
      },
      {
        label: pageText.POSITION,
        type: 'text',
        required: true,
        name: 'position',
        disabled: !enableEdit,
      },
      {
        label: pageText.MAJOR_RESPONSIBILITIES,
        type: 'textarea',
        required: true,
        name: 'majorResponsibilities',
        disabled: !enableEdit,
        maxCharacters: 750,
      },
    ],
    filesPanel: {
      title: pageText.FILES_PANEL.PANEL_TITLE,
      caption: pageText.FILES_PANEL.PANEL_CAPTION,
      files: [],
    },
  };

  const { switchText, initialForm } = employmentBoxInfo;

  async function handleSwitchChange(
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    await formik.setFieldValue('presentEmploymentIndicator', e.target.checked);
    await formik.setFieldValue('to', null);
  }

  const handleFileChange = (order: FILE_MANAGE_ORDER, id: string): boolean => {
    let newSupportingDocuments = [...supportingDocuments];
    switch (order) {
      case FILE_MANAGE_ORDER.REMOVE:
        // suppose document id is unique
        newSupportingDocuments = newSupportingDocuments.filter(file => {
          return file.id !== id;
        });
        break;
      case FILE_MANAGE_ORDER.UPLOAD:
        if (id) {
          newSupportingDocuments = [
            ...newSupportingDocuments,
            {
              id,
              name: '',
              link: '',
            },
          ];
        }
        break;
      default:
    }
    setSupportingDocuments(newSupportingDocuments);
    return true;
  };

  return (
    <Container>
      <ContentBox>
        <form onSubmit={handleReadySubmit(formik, employmentIndex)} noValidate>
          <Box sx={{ flexGrow: 1 }}>
            <SwitchButton
              checked={formik.values.presentEmploymentIndicator}
              disabled={!enableEdit}
              onChange={handleSwitchChange}
            />
            {switchText}
            <Grid container columnSpacing={4} rowSpacing={2}>
              {initialForm.map((item, index: number) => {
                return (
                  <Grid
                    key={index}
                    size={{
                      md: item.type === 'textarea' ? 12 : 6,
                    }}
                  >
                    {returnedComponent({ item, formik })}
                  </Grid>
                );
              })}
            </Grid>
            {enableEdit && (
              <div className="buttons-area">
                <Button variant="contained" type="submit">
                  {PAGE_TEXT.SAVE}
                </Button>
                {enableDelete && (
                  <Button onClick={handleDelete} variant="outlined">
                    {PAGE_TEXT.DELETE}
                  </Button>
                )}
              </div>
            )}
          </Box>
        </form>
        <div style={{ marginTop: '30px', marginLeft: '1rem' }}>
          <FilesPanel
            disable={!enableEdit}
            disableDeleteOnly={!enableDelete}
            title={pageText.FILES_PANEL.PANEL_TITLE}
            caption={pageText.FILES_PANEL.PANEL_CAPTION}
            files={supportingDocuments}
            handleFilesChange={handleFileChange}
            handleLoading={handleLoading}
            maxFileNumber={5}
          />
        </div>
      </ContentBox>
    </Container>
  );
};

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  margin-top: 20px;
  width: 100%;

  .MuiSwitch-root {
    margin: 1rem;
  }

  form {
    margin-bottom: 2rem;
    width: 100%;
  }

  .MuiGrid-root .MuiGrid-item {
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: column;
    padding: 0.5rem 1rem;
  }

  .MuiInputBase-root {
    margin: 0 !important;
  }

  .input-area {
    display: flex;
    flex-direction: column;
  }

  .buttons-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 0.5rem 1rem;

    button {
      margin: 0 1rem;
    }
  }
`;

const ContentBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;

  .MuiGrid-container {
    margin-top: 0;
    margin-left: 0;

    width: calc(100%);
  }

  .MuiGrid-root .MuiGrid-item {
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: column;
    padding: 0.5rem 1rem;
  }

  .MuiInputBase-root {
    margin: 0 !important;
  }

  .input-area {
    display: flex;
    flex-direction: column;
  }
`;
